import { useEffect, useState, type FormEvent } from 'react'
import {
  DIMENSIONS,
  DIMENSION_LABELS,
  EMPTY_EXPLORE_FILTERS,
  addPropertyFilter,
  countActiveFilters,
  isDimension,
  removePropertyFilter,
  toggleDimensionValue,
  type Dimension,
  type ExploreFilters,
} from '../../utils/exploreFilters'

interface EventFilterBarProps {
  filters: ExploreFilters
  onChange: (filters: ExploreFilters) => void
  /** Property keys present in the current results, from the facets endpoint. */
  propertyKeys: string[]
  formatValue: (dimension: Dimension, value: string) => string
  loadedCount: number
  totalCount: number | undefined
  hasMore: boolean
  isFetching: boolean
}

const SESSION_DEBOUNCE_MS = 350
const PROPERTY_FIELD_PREFIX = 'prop:'

const fieldClass =
  'w-full rounded-xl border border-white/10 bg-white/[0.035] px-3 py-2 text-sm text-white placeholder-gray-600 ' +
  'transition-colors focus:border-indigo-400/60 focus:outline-none focus:ring-1 focus:ring-indigo-400/30 hover:border-white/20'

const labelClass = 'mb-1.5 block text-xs font-medium uppercase tracking-wider text-gray-500'

export default function EventFilterBar({
  filters,
  onChange,
  propertyKeys,
  formatValue,
  loadedCount,
  totalCount,
  hasMore,
  isFetching,
}: EventFilterBarProps) {
  // The session field is debounced so a paste or keystroke does not fire a
  // query per character, but it must still follow external changes (clicking
  // a session in the table, removing its chip, landing on a shared URL).
  const [sessionDraft, setSessionDraft] = useState(filters.sessionId)
  useEffect(() => setSessionDraft(filters.sessionId), [filters.sessionId])
  useEffect(() => {
    if (sessionDraft.trim() === filters.sessionId) return
    const timer = setTimeout(() => onChange({ ...filters, sessionId: sessionDraft.trim() }), SESSION_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [sessionDraft, filters, onChange])

  const [field, setField] = useState<string>('event_name')
  const [value, setValue] = useState('')
  const isPropertyField = field.startsWith(PROPERTY_FIELD_PREFIX)
  const propertyKey = isPropertyField ? field.slice(PROPERTY_FIELD_PREFIX.length) : ''
  // A property filter may omit its value (match on presence); a column cannot.
  const canAdd = isPropertyField ? propertyKey.length > 0 : value.trim().length > 0

  const addFilter = (event: FormEvent) => {
    event.preventDefault()
    if (!canAdd) return
    const trimmed = value.trim()
    if (isPropertyField) {
      onChange(addPropertyFilter(filters, trimmed ? { key: propertyKey, value: trimmed } : { key: propertyKey }))
    } else if (isDimension(field)) {
      if (!(filters.dimensions[field] ?? []).includes(trimmed)) onChange(toggleDimensionValue(filters, field, trimmed))
    }
    setValue('')
  }

  const activeCount = countActiveFilters(filters)

  return (
    <div className="border-b border-white/10 px-5 py-4">
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
        <label className="block">
          <span className={labelClass}>Session</span>
          <input
            className={`${fieldClass} font-mono text-xs`}
            value={sessionDraft}
            placeholder="Paste a session ID, or click one in the table"
            onChange={event => setSessionDraft(event.target.value)}
          />
        </label>

        <form onSubmit={addFilter} className="block">
          <span className={labelClass}>Add filter</span>
          <div className="flex gap-2">
            <select
              aria-label="Filter field"
              className={`${fieldClass} w-auto min-w-0 max-w-[12rem] shrink`}
              value={field}
              onChange={event => setField(event.target.value)}
            >
              <optgroup label="Event fields">
                {DIMENSIONS.map(dimension => (
                  <option key={dimension} value={dimension}>
                    {DIMENSION_LABELS[dimension]}
                  </option>
                ))}
              </optgroup>
              {propertyKeys.length > 0 && (
                <optgroup label="Properties">
                  {propertyKeys.map(key => (
                    <option key={key} value={`${PROPERTY_FIELD_PREFIX}${key}`}>
                      {key}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <input
              aria-label="Filter value"
              className={fieldClass}
              value={value}
              placeholder={isPropertyField ? 'Value (blank = key is present)' : 'Exact value'}
              onChange={event => setValue(event.target.value)}
            />
            <button
              type="submit"
              disabled={!canAdd}
              className="mobile-tap-target shrink-0 rounded-xl bg-indigo-500/80 px-4 text-sm font-semibold text-white transition-colors hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-gray-500"
            >
              Add
            </button>
          </div>
        </form>
      </div>

      {activeCount > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {DIMENSIONS.flatMap(dimension =>
            (filters.dimensions[dimension] ?? []).map(item => (
              <FilterChip
                key={`${dimension}=${item}`}
                label={`${DIMENSION_LABELS[dimension]}: ${formatValue(dimension, item)}`}
                onRemove={() => onChange(toggleDimensionValue(filters, dimension, item))}
              />
            )),
          )}
          {filters.properties.map((property, index) => (
            <FilterChip
              key={`prop-${index}-${property.key}`}
              label={property.value === undefined ? `${property.key} is present` : `${property.key} = ${property.value}`}
              mono
              onRemove={() => onChange(removePropertyFilter(filters, index))}
            />
          ))}
          {filters.sessionId && (
            <FilterChip
              label={`Session: ${filters.sessionId}`}
              mono
              onRemove={() => onChange({ ...filters, sessionId: '' })}
            />
          )}
          <button
            type="button"
            onClick={() => onChange(EMPTY_EXPLORE_FILTERS)}
            className="rounded-full px-2 py-1 text-xs font-semibold text-gray-400 hover:text-white"
          >
            Clear all ({activeCount})
          </button>
        </div>
      )}

      <p className="mt-3 text-xs text-gray-500">
        {isFetching
          ? 'Loading events…'
          : `Showing ${loadedCount.toLocaleString()}${
              totalCount !== undefined ? ` of ${totalCount.toLocaleString()}` : ''
            } event${(totalCount ?? loadedCount) === 1 ? '' : 's'}${hasMore ? ' · more below' : ''}`}
      </p>
    </div>
  )
}

function FilterChip({ label, mono = false, onRemove }: { label: string; mono?: boolean; onRemove: () => void }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-full border border-indigo-400/30 bg-indigo-500/10 py-1 pl-3 pr-1 text-xs text-indigo-100">
      <span className={`truncate ${mono ? 'font-mono' : ''}`} title={label}>
        {label}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove filter ${label}`}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-indigo-200 hover:bg-white/10 hover:text-white"
      >
        ×
      </button>
    </span>
  )
}
